/** Safety alerts on a paper's address or defendant (SAFE-001…006): show, acknowledge, add. */
import { useState } from 'react';
import { SAFETY_CATEGORIES } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { fmtDate } from '../shared/format.ts';
import { ErrorBox, useAction } from '../shared/ui.tsx';

export interface SafetyAlert {
  id: string;
  target: 'address' | 'person';
  address_label: string | null;
  person_label: string | null;
  category: string;
  details: string | null;
  created_at: string;
  created_by_name: string;
  acknowledged_at: string | null;
}

export const unacknowledged = (p: any): SafetyAlert[] => ((p?.safetyAlerts ?? []) as SafetyAlert[]).filter((a) => !a.acknowledged_at);

export function SafetyAlerts({ paper, online, onChanged, compact }: { paper: any; online: boolean; onChanged: () => void; compact?: boolean }) {
  const alerts: SafetyAlert[] = paper?.safetyAlerts ?? [];
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ target: 'address', category: '', details: '' });
  const a = useAction();
  const ack = (id: string) => a.run(async () => {
    await api.post(`/api/alerts/${id}/ack`, { paperId: paper.id });
    onChanged();
  });
  const add = () => a.run(async () => {
    await api.post(`/api/papers/${paper.id}/alerts`, form);
    setAdding(false);
    setForm({ target: 'address', category: '', details: '' });
    onChanged();
  });
  return (
    <div className={`safety ${alerts.length ? 'has' : 'none'} ${compact ? 'compact' : ''}`} aria-label="Safety alerts">
      {alerts.length === 0 && !adding && <p className="safety-empty">No safety alerts on file for this address or person.</p>}
      <ul className="safety-list">
        {alerts.map((x) => (
          <li key={x.id} className={x.acknowledged_at ? 'acked' : 'new'}>
            <div className="safety-cat">⚠ {x.category}</div>
            {x.details && <div className="safety-det">{x.details}</div>}
            <div className="safety-meta">
              {x.target === 'person' ? `Person: ${x.person_label}` : `Address: ${x.address_label}`} · added by {x.created_by_name} {fmtDate(x.created_at)}
            </div>
            {!x.acknowledged_at && (
              <button className="btn sm danger" disabled={!online || a.busy} onClick={() => ack(x.id)}>
                {online ? 'I’ve read this' : 'Acknowledge when online'}
              </button>
            )}
          </li>
        ))}
      </ul>
      {adding ? (
        <form
          className="safety-add"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <div className="seg" role="group" aria-label="Alert applies to">
            <button type="button" className={form.target === 'address' ? 'active' : ''} onClick={() => setForm({ ...form, target: 'address' })}>
              This address
            </button>
            <button type="button" className={form.target === 'person' ? 'active' : ''} onClick={() => setForm({ ...form, target: 'person' })}>
              This person
            </button>
          </div>
          <select aria-label="Alert type" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required>
            <option value="">Alert type…</option>
            {SAFETY_CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <input aria-label="Alert details" placeholder="Details (e.g. two dogs loose in back yard)" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} maxLength={1000} />
          <div className="row gap">
            <button className="btn sm primary" disabled={a.busy || !form.category}>
              Save alert
            </button>
            <button type="button" className="btn sm ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button className="btn sm ghost safety-addbtn" disabled={!online} onClick={() => setAdding(true)} title={online ? '' : 'Needs a connection'}>
          + Add safety alert
        </button>
      )}
      <ErrorBox error={a.error} />
    </div>
  );
}
